import { createTheme } from '@mui/material';

// Single source for the brand hex (WS-B, finding #9). Exported so non-theme
// contexts that can't read the MUI palette at value-time — the hero tier
// registry, Mapbox paint expressions, and the handful of raw-hex render sites —
// reference one definition instead of re-hardcoding "#C8102E" across 12 files.
// The MUI palette below is built from these same constants.
export const BRAND = {
  red: '#C8102E',
  redLight: '#E8344F',
  redDark: '#9B0C23',
  navy: '#14213D',
  navyLight: '#1E3154',
  navyDark: '#0B1223',
  textPrimary: '#1A1D23',
  textSecondary: '#5A6070',
  white: '#FFFFFF',
};

// Navy-tinted shadow factory — all shadows use secondary navy as base, never pure black
const navyShadow = (opacity) => `rgba(20, 33, 61, ${opacity})`;

const shadows = [
  'none',
  `0 1px 2px ${navyShadow(0.04)}`,
  `0 1px 3px ${navyShadow(0.06)}`,
  `0 2px 4px ${navyShadow(0.06)}`,
  `0 2px 6px ${navyShadow(0.07)}`,
  `0 3px 8px ${navyShadow(0.08)}`,
  `0 4px 12px ${navyShadow(0.08)}`,
  `0 5px 14px ${navyShadow(0.09)}`,
  `0 6px 16px ${navyShadow(0.09)}`,
  `0 8px 20px ${navyShadow(0.10)}`,
  `0 8px 24px ${navyShadow(0.10)}`,
  `0 10px 28px ${navyShadow(0.11)}`,
  `0 12px 32px ${navyShadow(0.11)}`,
  `0 14px 36px ${navyShadow(0.12)}`,
  `0 16px 40px ${navyShadow(0.12)}`,
  `0 16px 44px ${navyShadow(0.13)}`,
  `0 18px 48px ${navyShadow(0.13)}`,
  `0 20px 52px ${navyShadow(0.14)}`,
  `0 22px 56px ${navyShadow(0.14)}`,
  `0 24px 60px ${navyShadow(0.15)}`,
  `0 26px 64px ${navyShadow(0.15)}`,
  `0 28px 68px ${navyShadow(0.16)}`,
  `0 30px 72px ${navyShadow(0.16)}`,
  `0 32px 76px ${navyShadow(0.17)}`,
  `0 34px 80px ${navyShadow(0.17)}`,
];

const theme = createTheme({
  palette: {
    primary: {
      main: BRAND.red,
      light: BRAND.redLight,
      dark: BRAND.redDark,
      contrastText: BRAND.white,
    },
    secondary: {
      main: BRAND.navy,
      light: BRAND.navyLight,
      dark: BRAND.navyDark,
      contrastText: BRAND.white,
    },
    text: {
      primary: BRAND.textPrimary,
      secondary: BRAND.textSecondary,
      disabled: '#9CA3AF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F8F9FC',
      subtle: '#F1F3F8',
      cream: '#F4EFE6',
      cinema: '#0A1020',
    },
    divider: 'rgba(20, 33, 61, 0.08)',
    grey: {
      50: '#F8F9FC',
      100: '#F1F3F8',
      200: '#E4E7EE',
      300: '#D1D5E0',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#5A6070',
      700: '#3D4350',
      800: '#1F2430',
      900: '#1A1D23',
    },
    error: {
      main: '#DC2626',
    },
    success: {
      main: '#16A34A',
    },
    warning: {
      main: '#D97706',
    },
    info: {
      main: '#2563EB',
    },
  },

  shadows,

  typography: {
    fontFamily: '"Outfit", "Helvetica Neue", Arial, sans-serif',
    fontFamilyMono: '"JetBrains Mono", "SF Mono", "Consolas", monospace',

    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.04em',
      lineHeight: 1.15,
      '@media (min-width:600px)': {
        fontSize: '3rem',
      },
    },
    // Oversized display tier — signature hero headlines only.
    h1Display: {
      fontFamily: '"Outfit", "Helvetica Neue", Arial, sans-serif',
      fontWeight: 700,
      fontSize: '4rem',
      letterSpacing: '-0.05em',
      lineHeight: 0.9,
      '@media (min-width:600px)': {
        fontSize: '5.5rem',
      },
      '@media (min-width:960px)': {
        fontSize: '7rem',
      },
    },
    // Giant mono numbers — countdowns, hero stats, counters.
    monoDisplay: {
      fontFamily: '"JetBrains Mono", "SF Mono", "Consolas", monospace',
      fontWeight: 500,
      fontSize: '4rem',
      letterSpacing: '-0.02em',
      lineHeight: 0.9,
      '@media (min-width:600px)': {
        fontSize: '6rem',
      },
      '@media (min-width:960px)': {
        fontSize: '9rem',
      },
    },
    // Outlined-only mono — used as texture for tickers and watermarks.
    monoOutline: {
      fontFamily: '"JetBrains Mono", "SF Mono", "Consolas", monospace',
      fontWeight: 500,
      fontSize: '3rem',
      letterSpacing: '-0.01em',
      lineHeight: 1,
      color: 'transparent',
      WebkitTextStroke: '1.5px currentColor',
      '@media (min-width:600px)': {
        fontSize: '4rem',
      },
      '@media (min-width:960px)': {
        fontSize: '5rem',
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.03em',
      lineHeight: 1.2,
      '@media (min-width:600px)': {
        fontSize: '2.25rem',
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.3,
      '@media (min-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.1rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.35,
      '@media (min-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0em',
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.75,
      letterSpacing: '0.01em',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em',
      fontWeight: 400,
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: '0.02em',
    },
    overline: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
      fontSize: '0.875rem',
    },
  },

  shape: {
    borderRadius: 12,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.02em',
          textTransform: 'none',
          transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          '&:active': {
            transform: 'scale(0.97)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 4px 12px ${navyShadow(0.12)}`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'transparent',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1rem',
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(20, 33, 61, 0.08)',
          boxShadow: 'none',
          transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          '&:hover': {
            boxShadow: `0 4px 12px ${navyShadow(0.08)}`,
            borderColor: 'rgba(20, 33, 61, 0.12)',
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },

    MuiChip: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
        },
        sizeSmall: {
          height: 26,
        },
        outlined: {
          borderColor: 'rgba(20, 33, 61, 0.15)',
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '8px !important',
          border: '1px solid rgba(20, 33, 61, 0.08)',
          '&::before': {
            display: 'none',
          },
          '&:not(:last-child)': {
            marginBottom: 8,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          minHeight: 52,
          '&.Mui-expanded': {
            minHeight: 52,
          },
        },
        content: {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 400,
          fontSize: '0.875rem',
          minHeight: 44,
          padding: '8px 16px',
          '&.Mui-selected': {
            fontWeight: 500,
          },
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          // Reset the global MuiPaper borderRadius (12) — an AppBar must be
          // square or its rounded corners expose the page behind the fixed bar.
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: '1px solid rgba(20, 33, 61, 0.08)',
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: 'rgba(20, 33, 61, 0.15)',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(20, 33, 61, 0.3)',
            },
          },
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1Display: 'h1',
          monoDisplay: 'span',
          monoOutline: 'span',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A1D23',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          padding: '6px 12px',
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
        },
        separator: {
          color: '#9CA3AF',
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(20, 33, 61, 0.06)',
        },
      },
    },

    MuiModal: {
      styleOverrides: {
        backdrop: {
          backgroundColor: 'rgba(11, 18, 35, 0.5)',
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.6875rem',
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          textDecorationColor: 'rgba(20, 33, 61, 0.3)',
          '&:hover': {
            textDecorationColor: 'currentColor',
          },
        },
      },
    },
  },
});

export default theme;
